import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import colors from "../../Constants/colors";
import { useNavigate } from "react-router-dom";
import {
  useDeleteKnowledgeBase,
  useKnowledgeList,
  useSingleKnowledge,
  useStopKnowledgeBase,
} from "../../Query/useKnowledge";
import { useState } from "react";
import KnowledgeBaseModal from "./KnowledgeBaseModal ";
import ConfirmationDialog from "../Common/ConfirmationDialog";
import { KnowledgeBaseStatus } from "../../Enums/companyStatus";
import Loading from "../Common/Loading";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { statusToStringKnowledgeBase } from "../../Helpers/stringHelpers";
import { useTranslation } from "react-i18next";

const KnowledgeBaseAll = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("createKnowledgeBase");
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [selectedKnowledgeBaseId, setSelectedKnowledgeBaseId] = useState<
    number | null
  >(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [stoppingId, setStoppingId] = useState<number | null>(null);
  const { mutate: deleteKnowledgeBase } = useDeleteKnowledgeBase();
  const { mutate: stopKnowledgeBase } = useStopKnowledgeBase();
  const { colors: workspace } = useWorkspaceColors();

  const handleModalClose = () => setIsModalOpen(false);

  const handleKnowledgeBaseName = (knowledgeBaseId: number) => {
    setSelectedKnowledgeBaseId(knowledgeBaseId);
    setIsModalOpen(true);
  };

  const handleConfirmationDialogClose = (confirmed: boolean) => {
    setConfirmationDialogOpen(false);
    if (confirmed && selectedKnowledgeBaseId) {
      deleteKnowledgeBase(selectedKnowledgeBaseId);
    }
  };

  const handleEditKnowledgeBase = (knowledgeBaseId: number) => {
    navigate(`/knowledge_base/edit/${knowledgeBaseId}`);
  };
  const { data, isLoading, isError } = useKnowledgeList();

  const handleStopKnowledgeBase = async (knowledgeBaseId: number) => {
    setStoppingId(knowledgeBaseId);
    stopKnowledgeBase(knowledgeBaseId, {
      onSettled: () => {
        setStoppingId(null);
      },
    });
  };

  const {
    data: selectedKnowledgeBase,
    isLoading: isKnowledgeBaseLoading,
    refetch: refetchKnowledgeBase,
  } = useSingleKnowledge(selectedKnowledgeBaseId ?? 0);

  if (isLoading) {
    return (
      <Loading
        backgroundColor={colors.gray100}
        color1={workspace.accent}
        color2={workspace.primary_dark}
      />
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 4, color: colors.red700 }}>
        Error: Failed to fetch knowledge base list
      </Box>
    );
  }

  const headerCellSx = {
    fontWeight: 600,
    color: colors.gray700,
    backgroundColor: colors.gray50,
    borderBottom: `1px solid ${colors.gray200}`,
    fontSize: "0.8rem",
  };

  const bodyCellSx = {
    color: colors.primary_dark,
    fontSize: "0.875rem",
    borderBottom: `1px solid ${colors.gray100}`,
  };

  const getStatusChipProps = (status: KnowledgeBaseStatus) => {
    switch (status) {
      case KnowledgeBaseStatus.PROCESSED:
        return {
          bg: colors.green50,
          color: colors.green700,
          border: colors.green200,
        };
      case KnowledgeBaseStatus.IN_PROGRESS:
        return {
          bg: colors.blue50,
          color: colors.blue700,
          border: colors.blue200,
        };
      case KnowledgeBaseStatus.STOPPED:
        return {
          bg: colors.orange50,
          color: colors.orange600,
          border: colors.orange200,
        };
      default:
        return {
          bg: colors.gray100,
          color: colors.gray700,
          border: colors.gray200,
        };
    }
  };

  return (
    <Box
      sx={{ minHeight: "calc(100vh - 64px)", backgroundColor: colors.gray50 }}
    >
      <Box sx={{ maxWidth: "1024px", mx: "auto", px: { xs: 2, sm: 3 }, py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: colors.white,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <StorageOutlinedIcon
                sx={{ fontSize: 32, color: workspace.primary_dark }}
              />
              <Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, color: workspace.primary_dark }}
                >
                  {t("createKnowledgeBase.title")}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.gray500 }}>
                  {data?.length ?? 0}
                </Typography>
              </Box>
            </Stack>

            <IconButton
              onClick={() => navigate("/knowledge_base/create")}
              sx={{
                bgcolor: workspace.accent,
                color: workspace.primary_dark,
                borderRadius: 1.5,
                px: 1.5,
                py: 1,
                gap: 0.75,
                "&:hover": { bgcolor: workspace.accent, opacity: 0.9 },
              }}
            >
              <AddCircleOutlineIcon sx={{ fontSize: 20 }} />
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, fontSize: "0.85rem" }}
              >
                {t("createKnowledgeBase.createNewKnowledgeBase")}
              </Typography>
            </IconButton>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: colors.white,
          }}
        >
          <TableContainer
            sx={{
              border: `1px solid ${colors.gray200}`,
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellSx, width: "40%" }}>
                    {t("createKnowledgeBase.name")}
                  </TableCell>
                  <TableCell sx={{ ...headerCellSx, width: "30%" }}>
                    {t("createKnowledgeBase.status")}
                  </TableCell>
                  <TableCell
                    sx={{ ...headerCellSx, width: "30%" }}
                    align="right"
                  >
                    {t("createKnowledgeBase.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      align="center"
                      sx={{
                        color: colors.gray500,
                        py: 6,
                        backgroundColor: colors.gray50,
                        fontSize: "0.875rem",
                      }}
                    >
                      {t("createKnowledgeBase.noKnowledgeBases")}
                    </TableCell>
                  </TableRow>
                ) : (
                  (data ?? []).map((item) => {
                    const statusProps = getStatusChipProps(item.status);
                    const disableEdit =
                      stoppingId === item.id ||
                      item.status === KnowledgeBaseStatus.STOPPED ||
                      item.status === KnowledgeBaseStatus.IN_PROGRESS;
                    const disableDelete =
                      stoppingId === item.id ||
                      item.status === KnowledgeBaseStatus.IN_PROGRESS;
                    return (
                      <TableRow
                        key={item.id}
                        sx={{
                          cursor: "pointer",
                          transition: "background-color 0.15s ease-in-out",
                          "&:hover": {
                            backgroundColor: `${workspace.primary_dark}08`,
                          },
                        }}
                      >
                        <TableCell
                          sx={bodyCellSx}
                          onClick={() => handleKnowledgeBaseName(item.id)}
                        >
                          {item.name}
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Chip
                              label={
                                stoppingId === item.id
                                  ? "Stopping"
                                  : statusToStringKnowledgeBase(item.status)
                              }
                              size="small"
                              sx={{
                                bgcolor: statusProps.bg,
                                color: statusProps.color,
                                border: `1px solid ${statusProps.border}`,
                                fontWeight: 500,
                                fontSize: "0.7rem",
                                height: 22,
                              }}
                            />
                            {item.status ===
                              KnowledgeBaseStatus.IN_PROGRESS && (
                              <>
                                <CircularProgress
                                  size={14}
                                  sx={{ color: workspace.accent }}
                                />
                                <Tooltip title="Stop Knowledge Base">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleStopKnowledgeBase(item.id);
                                    }}
                                    sx={{ color: workspace.accent }}
                                    disabled={
                                      stoppingId === item.id ||
                                      item.status !==
                                        KnowledgeBaseStatus.IN_PROGRESS
                                    }
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={bodyCellSx} align="right">
                          <Tooltip title="Edit">
                            <span>
                              <IconButton
                                size="small"
                                disabled={disableEdit}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditKnowledgeBase(item.id);
                                }}
                                sx={{
                                  color: workspace.accent,
                                  opacity: disableEdit ? 0.4 : 1,
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <span>
                              <IconButton
                                size="small"
                                disabled={disableDelete}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedKnowledgeBaseId(item.id);
                                  setConfirmationDialogOpen(true);
                                }}
                                sx={{
                                  color: colors.red600,
                                  opacity: disableDelete ? 0.4 : 1,
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <ConfirmationDialog
        open={confirmationDialogOpen}
        onClose={handleConfirmationDialogClose}
        title="Delete Knowledge Base"
        description="confirmation to delete a Knowledge Base"
        content="Are you sure you want to delete this Knowledge Base?"
        actions={[
          {
            label: "No",
            onClick: () => handleConfirmationDialogClose(false),
          },
          {
            label: "Yes",
            onClick: () => handleConfirmationDialogClose(true),
          },
        ]}
      />

      <KnowledgeBaseModal
        open={isModalOpen}
        onClose={handleModalClose}
        knowledgeBase={selectedKnowledgeBase ?? null}
        isLoading={isKnowledgeBaseLoading}
        onStopKnowledgeBase={handleStopKnowledgeBase}
        refetchKnowledgeBase={refetchKnowledgeBase}
      />
    </Box>
  );
};

export default KnowledgeBaseAll;
